import { Link } from '@tanstack/react-router'
import { ExternalLink, Pencil } from 'lucide-react'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { useListing } from '~/entities/listing/model/use-listings'
import { ListingHoursSummary } from '~/features/listings/components/view/listing-hours-view'
import { ImageGallery } from '~/features/shared/components/image-gallery'
import { ClampedContent } from '~/features/shared/components/view/clamped-content'
import { ContactLinks, hasContactLinks } from '~/features/shared/components/view/contact-links-view'
import { DetailList, DetailRow } from '~/features/shared/components/view/detail-row'
import * as m from '~/paraglide/messages'
import { countryDisplayName } from '~/shared/lib/countries'
import type { Listing } from '~/shared/types'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import { Skeleton } from '~/shared/ui/skeleton'

function mapsUrl(listing: Listing): string {
  const { coordinates, street, city, country } = listing.location
  const query = coordinates
    ? `${coordinates.lat},${coordinates.lng}`
    : encodeURIComponent(`${street}, ${city}, ${country}`)
  return `https://www.google.com/maps/search/?api=1&query=${query}`
}

export function ListingDetailsCard({ listingId }: { listingId: string }) {
  const { data: business } = useCurrentBusiness()
  const { data: listing, isLoading } = useListing(business?.id ?? '', listingId)

  if (isLoading || !business) return <Skeleton className="h-80 w-full rounded-xl" />
  if (!listing) return null

  const images = listing.images.map((image) => image.url)
  const contact = {
    phone: listing.phone || undefined,
    email: listing.email || undefined,
    website: listing.website || undefined,
    facebook: listing.facebook || undefined,
    instagram: listing.instagram || undefined,
    twitter: listing.twitter || undefined,
  }
  const { name, street, city, country } = listing.location

  return (
    <Card className="p-4 sm:p-6">
      <div className="mb-4 flex items-center justify-between gap-2">
        <h2 className="font-semibold font-serif text-lg">{m.details()}</h2>
        <Button asChild variant="ghost" size="sm">
          <Link to="/dashboard/listings/$listingId/edit" params={{ listingId }}>
            <Pencil />
            {m.edit()}
          </Link>
        </Button>
      </div>
      <DetailList>
        <DetailRow label={m.description()}>
          {listing.description ? (
            <ClampedContent className="max-w-prose whitespace-pre-wrap leading-relaxed">
              {listing.description}
            </ClampedContent>
          ) : (
            <p className="text-muted-foreground">{m.eventDetailNoDescription()}</p>
          )}
        </DetailRow>
        {listing.hours.length > 0 && (
          <DetailRow label={m.openingHours()}>
            <ListingHoursSummary hours={listing.hours} />
          </DetailRow>
        )}
        <DetailRow label={m.address()}>
          <div className="space-y-0.5">
            {name && name !== listing.name && <p className="font-medium">{name}</p>}
            <p className="text-muted-foreground">
              {[street, city, countryDisplayName(country)].filter(Boolean).join(', ')}
            </p>
            <a
              href={mapsUrl(listing)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-medium text-brand-subtle-foreground hover:underline"
            >
              {m.openInMaps()}
              <ExternalLink className="size-3.5" />
            </a>
          </div>
        </DetailRow>
        {hasContactLinks(contact) && (
          <DetailRow label={m.placeDetailContact()}>
            <ContactLinks {...contact} />
          </DetailRow>
        )}
        {images.length > 0 && (
          <DetailRow label={m.placeDetailGallery()}>
            <ImageGallery
              images={images}
              label={listing.name}
              className="grid-cols-5 gap-1.5 sm:grid-cols-6 lg:grid-cols-8"
            />
          </DetailRow>
        )}
      </DetailList>
    </Card>
  )
}
